import { Canvas } from "../types/canvasTypes";
import { OBJECT_TYPE, VIEWPORT_MODIFIED_EVENT } from "../constants/canvasConstants";
import { BackgroundImageFillMode, ObjectExtensionFactory, ManageObjectDataFactory } from "../types/canvasTypes";
import { BackgroundImage, ViewportShape } from "../types/objectTypes";
import {
  findBackgroundImage,
  updateBackgroundWithImage,
  createBackgroundImageFromImage,
  calculateBackgroundImageOptions,
} from "../utils/backgroundUtils";
import { getObjectJsonData, deleteActiveObjects, loadImage } from "../utils/canvasUtils";
import { isImage } from "../utils/imageUtils";
import { findViewportShape } from "../utils/viewportUtils";
import { executeAsyncHistoryTransaction, triggerObjectModifiedEvent } from "../helpers/canvasHelpers";

export type BackgroundOptions = Partial<{
  imageUrl: string | null;
  fillMode: BackgroundImageFillMode;
  backgroundColor: string;
  imageOpacity: number;
}>;

const NAME = OBJECT_TYPE.background;

export type ViewportModificationHandler = (viewPort: Record<string, unknown>) => void;
export type SetImageAsBackgroundFunction = () => Promise<NonNullable<
  Pick<BackgroundOptions, "imageUrl" | "fillMode">
> | null>;

export const createBackgroundExtension: ObjectExtensionFactory<
  void,
  BackgroundOptions,
  {
    handleViewportModification: ViewportModificationHandler;
    setImageAsBackgroundFunctionFactory: (fn: SetImageAsBackgroundFunction) => void;
  }
> = (options) => {
  let handleViewportModified: ((canvas: Canvas) => void) | undefined;

  return {
    name: NAME,
    onInit: (canvas) => {
      options.setImageAsBackgroundFunctionFactory(createSetAsBackgroundImageFunction(canvas));

      handleViewportModified = () => {
        const viewPort = findViewportShape(canvas);
        const backgroundImage = findBackgroundImage(canvas);

        if (viewPort) {
          options.handleViewportModification(getObjectJsonData(viewPort));
        }

        if (!viewPort || !backgroundImage) return;

        recalculateAndChangeBackgroundOptionsIfNeeded({
          backgroundImage,
          viewPort,
          fill: backgroundImage.backgroundImageFillMode,
        });
      };

      canvas.on(VIEWPORT_MODIFIED_EVENT, handleViewportModified as any);
    },
    onDestroy: (canvas) => {
      canvas.off(VIEWPORT_MODIFIED_EVENT, handleViewportModified as any);
    },
    onAddObject() {},
    async onModifyObject({ canvas, payload: { imageUrl, fillMode, imageOpacity, backgroundColor } }) {
      const viewPort = findViewportShape(canvas);

      if (!viewPort) return;

      if (backgroundColor !== undefined) {
        viewPort.set("fill", backgroundColor);
      }

      await modifyBackgroundImage(canvas, { imageUrl, imageOpacity, fillMode }, viewPort);
      requestRerenderAndEmitModificationEvent(canvas);
    },
  };
};

export const createModifyBackgroundAction: ManageObjectDataFactory<BackgroundOptions> = (payload) => ({
  name: NAME,
  payload,
});

function createSetAsBackgroundImageFunction(canvas: Canvas) {
  return async () => {
    const activeObject = canvas.getActiveObject();

    if (!activeObject || !isImage(activeObject)) return null;

    const imageUrl = activeObject.getSrc();

    const options: BackgroundOptions = {
      imageUrl,
      fillMode: "cover",
    };

    await executeAsyncHistoryTransaction(canvas, async () => {
      deleteActiveObjects(canvas);

      await modifyBackgroundImage(canvas, options);
      requestRerenderAndEmitModificationEvent(canvas);
    });

    return options;
  };
}

async function modifyBackgroundImage(
  canvas: Canvas,
  options: Pick<BackgroundOptions, "imageUrl" | "imageOpacity" | "fillMode">,
  viewPort = findViewportShape(canvas)
) {
  const { imageUrl, imageOpacity, fillMode } = options;

  if (!viewPort) return;

  let backgroundImage = findBackgroundImage(canvas);
  if (imageUrl === null && backgroundImage) {
    canvas.remove(backgroundImage);
  } else {
    backgroundImage = await changeOrCreateBackgroundImageIfNeeded({
      canvas,
      backgroundImage,
      imageUrl,
    });

    if (imageOpacity !== undefined && backgroundImage) {
      backgroundImage.set("opacity", imageOpacity);
    }

    recalculateAndChangeBackgroundOptionsIfNeeded({ backgroundImage, viewPort, fill: fillMode });
  }
}

function requestRerenderAndEmitModificationEvent(canvas: Canvas) {
  canvas.requestRenderAll();
  triggerObjectModifiedEvent(canvas);
}

async function changeOrCreateBackgroundImageIfNeeded({
  backgroundImage,
  canvas,
  imageUrl,
}: {
  canvas: Canvas;
  imageUrl?: string | null;
  backgroundImage?: BackgroundImage;
}) {
  if (!imageUrl) return backgroundImage;

  const image = await loadImage(imageUrl);

  if (backgroundImage) {
    return updateBackgroundWithImage({ backgroundImage, image, canvas });
  }

  return createBackgroundImageFromImage({
    canvas,
    image,
  });
}

function recalculateAndChangeBackgroundOptionsIfNeeded({
  fill,
  backgroundImage,
  viewPort,
}: {
  viewPort: ViewportShape;
  backgroundImage?: BackgroundImage;
  fill?: BackgroundImageFillMode;
}) {
  if (!fill || !backgroundImage) return;

  const { width: viewPortWidth, height: viewPortHeight } = viewPort;
  const { width: backgroundImageWidth, height: backgroundImageHeight } = backgroundImage;
  if (
    viewPortWidth === undefined ||
    viewPortHeight === undefined ||
    backgroundImageWidth === undefined ||
    backgroundImageHeight === undefined
  )
    return;

  const options = calculateBackgroundImageOptions({
    backgroundImageWidth,
    backgroundImageHeight,
    viewPortWidth,
    viewPortHeight,
    fill,
  });

  backgroundImage.set({
    ...options,
    backgroundImageFillMode: fill,
  });
}
